import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import FeatureNotAvailable from '../components/FeatureNotAvailable';

/**
 * FeatureFlagRoute Component
 * Protects routes based on feature flags
 * 
 * @param {string} feature - The feature flag name required to access this route
 * @param {React.ReactNode} children - Child components to render if feature is enabled
 * @param {string} redirectTo - Optional redirect path if feature is disabled (defaults to showing FeatureNotAvailable)
 * @param {React.Component} fallback - Optional custom fallback component
 * 
 * @example
 * <Route 
 *   path="/proposals" 
 *   element={
 *     <FeatureFlagRoute feature="proposals">
 *       <ProposalsPage />
 *     </FeatureFlagRoute>
 *   } 
 * />
 */
export const FeatureFlagRoute = ({ 
  feature, 
  children, 
  redirectTo = null,
  fallback: FallbackComponent = null 
}) => {
  const { hasFeature } = useFeatureFlags();
  const location = useLocation();
  
  // Check if feature is enabled
  const isEnabled = hasFeature(feature);
  
  // If feature is enabled, render children
  if (isEnabled) {
    return <>{children}</>;
  }
  
  // If redirect path is provided, redirect there
  if (redirectTo) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }
  
  // If custom fallback is provided, render it
  if (FallbackComponent) {
    return <FallbackComponent featureName={feature} />;
  }
  
  // Default: show feature not available message
  return <FeatureNotAvailable featureName={feature} />;
};
